import { FC, createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import { useLoggedInUser } from 'dibs-buyer-layout/exports/useLoggedInUser';
import { usePersistedWalletAddress } from 'dibs-crypto-utils/exports/usePersistedWalletAddress';
import serverVars from 'server-vars';
import { graphql, useRefetchableFragment } from 'react-relay';

import { useNftProfileOwner_viewer$key as viewer$key } from './__generated__/useNftProfileOwner_viewer.graphql';
import { useNftProfileOwnerRefetchQuery as RefetchQuery } from './__generated__/useNftProfileOwnerRefetchQuery.graphql';

type Context = {
    isProfileOwner: boolean;
    loggedInUserId: string | null;
    canImport: boolean;
    walletAddress: string | null;
};

const nftProfileOwnerContext = createContext<Context | null>(null);

export const useNftProfileOwner = (): Context => {
    const context = useContext(nftProfileOwnerContext);
    return (
        context || {
            isProfileOwner: false,
            canImport: false,
            loggedInUserId: null,
            walletAddress: null,
        }
    );
};

export const getIsProfileOwner = (
    loggedInUserId: string | null,
    profileUserId: string | null
): boolean => !!loggedInUserId && loggedInUserId === profileUserId;

export const NftProfileOwnerProvider: FC<{
    serverIsProfileOwner?: boolean | null;
    profileUserId?: string | null;
    viewer: viewer$key | null;
    children: ReactNode;
}> = ({ profileUserId = null, serverIsProfileOwner, viewer: viewerRef = null, children }) => {
    const clientIsProfileOwner = serverVars.get('isProfileOwner') as boolean | undefined;
    const loggedInUserId = useLoggedInUser();
    const isProfileOwner =
        serverIsProfileOwner ||
        clientIsProfileOwner ||
        getIsProfileOwner(loggedInUserId, profileUserId);

    const [viewer, refetch] = useRefetchableFragment<RefetchQuery, viewer$key>(
        graphql`
            fragment useNftProfileOwner_viewer on Viewer
            @refetchable(queryName: "useNftProfileOwnerRefetchQuery")
            @argumentDefinitions(
                fetchUser: { type: "Boolean", defaultValue: false }
                loggedInUserId: { type: "String", defaultValue: "" }
            ) {
                ...usePersistedWalletAddress_viewer
                    @arguments(fetchUser: $fetchUser, userId: $loggedInUserId)
                loggedInUser: user(userId: $loggedInUserId) @include(if: $fetchUser) {
                    canImport: hasPermission(name: "NFT_IMPORT_CONTRACT_SELF_SERVE")
                }
            }
        `,
        viewerRef
    );
    const { persistedAddress } = usePersistedWalletAddress({ viewer });

    useEffect(() => {
        if (loggedInUserId) refetch({ loggedInUserId, fetchUser: !!loggedInUserId });
    }, [loggedInUserId, isProfileOwner, refetch]);
    const valueMemo = useMemo(
        () => ({
            isProfileOwner,
            loggedInUserId,
            canImport: isProfileOwner && !!viewer?.loggedInUser?.canImport,
            walletAddress: isProfileOwner ? persistedAddress : null,
        }),
        [isProfileOwner, loggedInUserId, viewer, persistedAddress]
    );
    return (
        <nftProfileOwnerContext.Provider value={valueMemo}>
            {children}
        </nftProfileOwnerContext.Provider>
    );
};
